const BASE = "";

export interface AuthenticatedUser {
  id?: string;
  userId?: string;
  email: string;
  tenantId: string;
  role: string;
  name?: string;
  passwordChangeRequired?: boolean;
}

export interface PlanFeature {
  featureKey: string;
  kind: "flag" | "quota";
  enabled: boolean;
  limit?: number | null;
  unit?: string;
  description?: string;
}

export interface PlanEntry {
  planId: string;
  tier: string;
  displayName: string;
  description: string;
  features: PlanFeature[];
  metadata?: Readonly<Record<string, unknown>>;
  active?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface PlanPreviewInput {
  action: "update" | "archive" | "publish";
  tier?: string;
  displayName?: string;
  description?: string;
  features?: PlanFeature[];
  metadata?: Readonly<Record<string, unknown>>;
  active?: boolean;
  sampleLimit?: number;
}

export interface PlanImpactPreview {
  dryRun: true;
  action: PlanPreviewInput["action"];
  planId: string;
  before: Readonly<Record<string, unknown>>;
  after: Readonly<Record<string, unknown>>;
  impact: {
    activeSubscriptionCount: number;
    affectedTenantIds: readonly string[];
    hasMoreAffectedTenants: boolean;
    featureChanges: {
      added: readonly string[];
      removed: readonly string[];
      changed: readonly string[];
    };
    metadataChangedKeys: readonly string[];
    visibilityChanged: boolean;
    billingChanged: boolean;
    risks: readonly string[];
  };
}

export interface SubscriptionEntry {
  subscriptionId: string;
  tenantId: string;
  planId: string;
  status: string;
  billingProvider?: string;
  externalSubscriptionId?: string;
  trialEndsAt?: string;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  cancelAtPeriodEnd: boolean;
  metadata?: Readonly<Record<string, unknown>>;
}

export interface TenantEntitlementSnapshot {
  tenantId: string;
  planId: string;
  planTier: string;
  features: Record<string, {
    featureKey: string;
    kind: "flag" | "quota";
    enabled: boolean;
    limit?: number | null;
    unit?: string;
    source: "plan" | "override" | "default";
  }>;
}

export interface WorkspaceSubscriptionResult {
  subscription: SubscriptionEntry;
  plan: PlanEntry | null;
  entitlements: TenantEntitlementSnapshot;
}

export interface WorkspaceUsageOverview {
  quotas: readonly { warningLevel: string }[];
  summaries: readonly unknown[];
}

export interface PlatformWorkspaceOnboardingBlocker {
  blockerId: string;
  severity: "info" | "warning" | "critical";
  message: string;
}

export interface PlatformWorkspaceOnboardingSummary {
  completed: boolean;
  completedAt?: string;
  updatedAt?: string;
  blockerCount: number;
  blockers: PlatformWorkspaceOnboardingBlocker[];
  activationEventCount: number;
  lastActivationEvent?: {
    eventId: string;
    eventType: "workspace.created" | "model_provider.configured" | "starter_template.deployed" | "first_run.completed" | "connector_or_sandbox.activated" | "approval_or_eval.configured" | "release.promoted" | "onboarding.completed";
    source: string;
    occurredAt: string;
    metadata: Readonly<Record<string, unknown>>;
  };
}

export interface PlatformWorkspaceSummary {
  tenantId: string;
  slug: string;
  name: string;
  status: string;
  planId: string;
  planTier: string;
  memberCount: number;
  isolationTier: "shared-saas" | "dedicated-execution" | "dedicated-deployment";
  usage: WorkspaceUsageOverview;
  entitlements: {
    enabledFeatureCount: number;
    quotaFeatureCount: number;
  };
  onboarding?: PlatformWorkspaceOnboardingSummary;
  createdAt: string;
  updatedAt: string;
}

export type TransactionalEmailProvider = "log" | "resend" | "sendgrid" | "smtp";

export interface PlatformTransactionalEmailSettings {
  provider: TransactionalEmailProvider;
  fromEmail: string;
  appBaseUrl: string;
  resendConfigured: boolean;
  sendGridConfigured: boolean;
  smtpConfigured: boolean;
  smtpHost?: string;
  smtpPort?: number;
  smtpUsername?: string;
  smtpSecure: boolean;
  smtpPasswordConfigured: boolean;
  source: "platform-secret" | "env" | "default";
  resendLast4?: string;
  sendGridLast4?: string;
  smtpPasswordLast4?: string;
  updatedAt?: string;
}

export interface PlatformTransactionalEmailSettingsInput {
  provider: TransactionalEmailProvider;
  fromEmail: string;
  appBaseUrl: string;
  resendApiKey?: string;
  sendGridApiKey?: string;
  smtpHost?: string;
  smtpPort?: number;
  smtpUsername?: string;
  smtpPassword?: string;
  smtpSecure?: boolean;
  reason: string;
}

export interface TransactionalEmailDeliveryResult {
  provider: TransactionalEmailProvider;
  delivered: boolean;
  messageId?: string;
}

export interface PlatformTransactionalEmailTestResult {
  delivery: TransactionalEmailDeliveryResult;
  settings: PlatformTransactionalEmailSettings;
}

export interface PlatformWorkspaceActivationPlanBucket {
  planId: string;
  planTier: string;
  workspaceCount: number;
  completedCount: number;
  pendingCount: number;
  blockedCount: number;
  activationEventCount: number;
  completionRate: number;
}

export interface PlatformWorkspaceActivationBlockerBucket {
  blockerId: string;
  severity: PlatformWorkspaceOnboardingBlocker["severity"];
  count: number;
  sampleTenantIds: string[];
}

export interface PlatformWorkspaceActivationSourceBucket {
  source: string;
  count: number;
  latestOccurredAt?: string;
}

export interface PlatformWorkspaceActivationRecentCompletion {
  tenantId: string;
  workspaceName: string;
  planId: string;
  source: string;
  occurredAt: string;
}

export interface PlatformWorkspaceActivationAnalytics {
  workspaceCount: number;
  completedCount: number;
  pendingCount: number;
  blockedCount: number;
  activationEventCount: number;
  completionRate: number;
  planBuckets: PlatformWorkspaceActivationPlanBucket[];
  blockerBuckets: PlatformWorkspaceActivationBlockerBucket[];
  sourceBuckets: PlatformWorkspaceActivationSourceBucket[];
  recentCompletions: PlatformWorkspaceActivationRecentCompletion[];
}

export interface WorkspaceLifecycleResult {
  workspace: {
    summary: PlatformWorkspaceSummary;
    [key: string]: unknown;
  };
}

export interface PlatformAuditEvent {
  entryId: string;
  timestamp: string;
  agentId: string;
  tenantId: string;
  action: string;
  resource: string;
  decision: "allow" | "deny";
  matchedRuleId: string;
  reason: string;
  traceId: string;
}

export interface PlatformFeatureCatalogSource {
  planId: string;
  displayName: string;
  tier: string;
  active: boolean;
  kind: "flag" | "quota";
  enabled: boolean;
  limit?: number | null;
  unit?: string;
  description?: string;
}

export interface PlatformFeatureCatalogEntry {
  featureKey: string;
  kind: "flag" | "quota" | "mixed";
  description?: string;
  planCount: number;
  enabledPlanIds: string[];
  quotaPlans: Array<{ planId: string; limit?: number | null; unit?: string }>;
  sources: PlatformFeatureCatalogSource[];
}

export interface PlatformModelProviderCredentialEntry {
  providerId: string;
  displayName: string;
  configured: boolean;
  source: "env" | "platform-secret" | "missing";
  last4?: string;
  updatedAt?: string;
  health: {
    available: boolean;
    latencyMs: number;
    errorRate: number;
    lastCheckedAt: string;
  };
  models: string[];
}

export interface PackPublisher {
  publisherId: string;
  displayName: string;
  contactEmail: string;
  trustTier: "verified" | "unverified";
  status: "pending" | "verified" | "suspended";
  publicKeyBase64: string;
  createdAt: string;
  verifiedAt?: string;
  verifiedBy?: string;
  notes?: string;
}

export interface PackRevocation {
  packId: string;
  version: string;
  reason: string;
  revokedAt: string;
  revokedBy?: string;
}

export type MarketplacePackReviewStatus = "pending" | "community_review" | "approved" | "changes_requested" | "rejected";
export type MarketplaceBuyerReviewStatus = "pending" | "approved" | "rejected";

export interface MarketplacePackEntry {
  packId: string;
  version: string;
  publisherId: string;
  status: "active" | "yanked";
  publishedAt: string;
  revoked: boolean;
  review: {
    status: MarketplacePackReviewStatus;
    reviewedAt?: string;
    reviewedBy?: string;
    notes?: string;
    evidenceUrls: readonly string[];
  };
  commercial?: Readonly<Record<string, unknown>>;
  signed?: Readonly<Record<string, unknown>>;
}

export interface MarketplacePackInstallFailureReasonBucket {
  reasonCode: string;
  reason: string;
  count: number;
  latestOccurredAt: string;
}

export type MarketplacePackActivationSection = "dlp" | "approvals" | "connectors" | "evaluations" | "releaseGates" | "trustDossier" | "roiAssumptions" | "dashboards";

export interface MarketplacePackActivationSectionEntry {
  section: MarketplacePackActivationSection;
  status: "applied" | "pending" | "skipped";
  itemCount: number;
  summary: string;
}

export interface MarketplacePackActivationBlockerBucket {
  section: MarketplacePackActivationSection;
  count: number;
  latestOccurredAt: string;
  samplePackIds: string[];
}

export interface MarketplacePackInstallPackAnalytics {
  packId: string;
  version?: string;
  publisherId?: string;
  qualityTier?: string;
  attemptCount: number;
  installCount: number;
  failedInstallCount: number;
  activationCompleteCount: number;
  activationPendingCount: number;
  activationSuccessRate: number;
  activationBlockers: MarketplacePackActivationBlockerBucket[];
  failureReasons: MarketplacePackInstallFailureReasonBucket[];
}

export interface MarketplacePackInstallAnalyticsRecentEvent {
  eventId: string;
  tenantId: string;
  packId: string;
  version?: string;
  publisherId?: string;
  qualityTier?: string;
  status: "succeeded" | "failed";
  activationStatus?: "activated" | "pending";
  activationSections: MarketplacePackActivationSectionEntry[];
  reasonCode?: string;
  reason?: string;
  occurredAt: string;
  actorId?: string;
}

export interface MarketplacePackInstallAnalytics {
  generatedAt: string;
  tenantId?: string;
  packId?: string;
  attemptCount: number;
  installCount: number;
  failedInstallCount: number;
  activationCompleteCount: number;
  activationPendingCount: number;
  activationSuccessRate: number;
  activationBlockers: MarketplacePackActivationBlockerBucket[];
  failureReasons: MarketplacePackInstallFailureReasonBucket[];
  packs: MarketplacePackInstallPackAnalytics[];
  recentEvents: MarketplacePackInstallAnalyticsRecentEvent[];
}

export interface MarketplacePackReviewEntry {
  reviewId: string;
  tenantId: string;
  packId: string;
  version: string;
  rating: number;
  title?: string;
  body?: string;
  status: MarketplaceBuyerReviewStatus;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  moderatedAt?: string;
  moderatedBy?: string;
  moderationNotes?: string;
}

export type ConnectorPluginStatus = "pending" | "approved" | "suspended" | "revoked";
export type ConnectorPluginAdapter = "http" | "process" | "mcp";

export interface ConnectorPluginImpactSummary {
  tenantId: string;
  pluginId: string;
  version: string;
  evaluatedAt: string;
  actionCount: number;
  triggerEventCount: number;
  affectedConnectionCount: number;
  activeConnectionCount: number;
  disabledConnectionCount: number;
  connectionSamples: readonly Readonly<Record<string, unknown>>[];
  hasMoreConnections: boolean;
  affectedAutomationTriggerCount: number;
  automationTriggerSamples: readonly Readonly<Record<string, unknown>>[];
  hasMoreAutomationTriggers: boolean;
  affectedAgentIds: readonly string[];
  affectedPackInstallCount: number;
  packInstallSamples: readonly Readonly<Record<string, unknown>>[];
  hasMorePackInstalls: boolean;
  recentTriggerEventCount: number;
  recentTriggerEventSamples: readonly Readonly<Record<string, unknown>>[];
  hasMoreRecentTriggerEvents: boolean;
  coverage: Readonly<Record<string, boolean>>;
  warnings: readonly string[];
}

export interface ConnectorPluginEntry {
  tenantId: string;
  pluginId: string;
  version: string;
  displayName: string;
  description: string;
  adapter: ConnectorPluginAdapter;
  status: ConnectorPluginStatus;
  manifestHash: string;
  definition?: Readonly<Record<string, unknown>>;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  reviewedAt?: string;
  reviewedBy?: string;
  revokedAt?: string;
  revokedBy?: string;
  reason?: string;
  notes?: string;
  impact?: ConnectorPluginImpactSummary;
}

export interface ConnectorPluginCertification {
  pluginId?: string;
  version?: string;
  status?: string;
  generatedAt?: string;
  checks?: readonly unknown[];
  [key: string]: unknown;
}

export interface AgentReleaseEntry {
  releaseId: string;
  agentId: string;
  agentName?: string;
  version?: string;
  environment?: "dev" | "staging" | "production";
  status?: string;
  createdAt?: string;
  updatedAt?: string;
  [key: string]: unknown;
}

export interface RolloutPlanEntry {
  agentId: string;
  environment: "dev" | "staging" | "production";
  splits: readonly unknown[];
  updatedAt: string;
  [key: string]: unknown;
}

export interface PlatformLocalRunnerFleetSummary {
  totalLocalRunnerCount: number;
  onlineLocalRunnerCount: number;
  offlineLocalRunnerCount: number;
  outdatedLocalRunnerCount: number;
  localRunnerAssignmentCount: number;
  localRunnerQueuedTaskCount: number;
  localRunnerCurrentTaskCount: number;
  localRunnerTaskCapacity: number;
  localRunnerSaturationPercent: number | null;
}

export interface PlatformLocalRunnerWorkspacePosture {
  workspace: PlatformWorkspaceSummary;
  health: "healthy" | "attention" | "critical";
  localRunners: {
    totalRunnerCount: number;
    onlineRunnerCount: number;
    offlineRunnerCount: number;
    pendingRunnerCount: number;
    retiredRunnerCount: number;
    outdatedRunnerCount: number;
    assignmentCount: number;
    runningAssignmentCount: number;
    drainingAssignmentCount: number;
    failedAssignmentCount: number;
    queuedTaskCount: number;
    runningTaskCount: number;
    failedTaskCount: number;
    currentTaskCount: number;
    taskCapacity: number;
    saturationPercent: number | null;
    latestHeartbeatAt?: string;
    latestVersion?: string;
    versionBreakdown: readonly { version: string; runnerCount: number }[];
    attentionItems: readonly string[];
  };
  attentionItems: readonly string[];
}

export interface PlatformFleetOverviewEntry {
  generatedAt: string;
  summary: PlatformLocalRunnerFleetSummary & Readonly<Record<string, unknown>>;
  workspaces: PlatformLocalRunnerWorkspacePosture[];
}

export interface SubscriptionUpdateInput {
  planId?: string;
  status?: "trialing" | "active" | "past_due" | "canceled";
  billingProvider?: string | null;
  externalSubscriptionId?: string | null;
  trialEndsAt?: string | null;
  currentPeriodStart?: string | null;
  currentPeriodEnd?: string | null;
  cancelAtPeriodEnd?: boolean;
  metadata?: Readonly<Record<string, unknown>>;
  reason: string;
}

export interface TenantEntitlementOverride {
  overrideId: string;
  tenantId: string;
  featureKey: string;
  enabled?: boolean;
  limit?: number | null;
  unit?: string;
  reason?: string;
  expiresAt?: string;
  metadata: Readonly<Record<string, unknown>>;
  createdAt: string;
  updatedAt: string;
}

export interface EntitlementOverrideGovernanceInput {
  ticketId: string;
  approvedBy: string;
  riskTier: "low" | "medium" | "high" | "critical";
  approvalId?: string;
  comment?: string;
}

export interface EntitlementOverrideInput {
  featureKey: string;
  enabled?: boolean;
  limit?: number | null;
  unit?: string;
  expiresAt?: string;
  governance?: EntitlementOverrideGovernanceInput;
  metadata?: Readonly<Record<string, unknown>>;
  reason: string;
}

export interface EntitlementOverrideResult {
  override: TenantEntitlementOverride;
  entitlements: TenantEntitlementSnapshot;
}

class ApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(method: string, path: string, token: string | null, body?: unknown): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    credentials: "include",
  });
  const payload = await response.json().catch(() => ({})) as unknown;
  if (!response.ok) {
    const message = payload && typeof payload === "object" && "error" in payload && typeof payload.error === "string"
      ? payload.error
      : `HTTP ${response.status}`;
    throw new ApiError(message, response.status);
  }
  return payload as T;
}

function buildQueryString(values: Record<string, string | number | undefined>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(values)) {
    if (value !== undefined && value !== "") {
      params.set(key, String(value));
    }
  }
  const query = params.toString();
  return query ? `?${query}` : "";
}

export const api = {
  login: (email: string, password: string) =>
    request<{ user: AuthenticatedUser; token: string }>("POST", "/api/v1/auth/login", null, { email, password }),
  me: (token: string) => request<{ user: AuthenticatedUser }>("GET", "/api/v1/auth/me", token),
  changePassword: (token: string, input: { currentPassword: string; newPassword: string }) =>
    request<{ user: AuthenticatedUser; token: string; csrfToken?: string }>("POST", "/api/v1/auth/change-password", token, input),
  plans: (token: string) => request<{ plans: PlanEntry[] }>("GET", "/api/v1/platform/plans", token),
  features: (token: string) => request<{ features: PlatformFeatureCatalogEntry[] }>("GET", "/api/v1/platform/features", token),
  platformModelProviderCredentials: (token: string) =>
    request<{ providers: PlatformModelProviderCredentialEntry[] }>("GET", "/api/v1/platform/model-provider-credentials", token),
  rotatePlatformOpenAiCredential: (token: string, input: { apiKey: string; reason: string }) =>
    request<{ provider: PlatformModelProviderCredentialEntry }>("POST", "/api/v1/platform/model-provider-credentials/openai/rotate", token, input),
  platformTransactionalEmailSettings: (token: string) =>
    request<{ settings: PlatformTransactionalEmailSettings }>("GET", "/api/v1/platform/transactional-email", token),
  updatePlatformTransactionalEmailSettings: (token: string, input: PlatformTransactionalEmailSettingsInput) =>
    request<{ settings: PlatformTransactionalEmailSettings }>("PUT", "/api/v1/platform/transactional-email", token, input),
  sendPlatformTransactionalEmailTest: (token: string, input: { toEmail: string }) =>
    request<PlatformTransactionalEmailTestResult>("POST", "/api/v1/platform/transactional-email/test", token, input),
  plan: (token: string, planId: string) => request<{ plan: PlanEntry }>("GET", `/api/v1/platform/plans/${encodeURIComponent(planId)}`, token),
  createPlan: (token: string, input: Omit<PlanEntry, "createdAt" | "updatedAt"> & { reason: string }) =>
    request<{ plan: PlanEntry }>("POST", "/api/v1/platform/plans", token, input),
  updatePlan: (token: string, planId: string, input: Partial<PlanEntry> & { reason: string }) =>
    request<{ plan: PlanEntry }>("PUT", `/api/v1/platform/plans/${encodeURIComponent(planId)}`, token, input),
  archivePlan: (token: string, planId: string, reason: string) =>
    request<{ plan: PlanEntry }>("POST", `/api/v1/platform/plans/${encodeURIComponent(planId)}/archive`, token, { reason }),
  clonePlan: (token: string, planId: string, input: { targetPlanId: string; displayName?: string; reason: string }) =>
    request<{ plan: PlanEntry }>("POST", `/api/v1/platform/plans/${encodeURIComponent(planId)}/clone`, token, input),
  previewPlan: (token: string, planId: string, input: PlanPreviewInput) =>
    request<{ preview: PlanImpactPreview }>("POST", `/api/v1/platform/plans/${encodeURIComponent(planId)}/preview`, token, input),
  workspaces: (token: string, filters: { query?: string; status?: string; limit?: number } = {}) =>
    request<{ workspaces: PlatformWorkspaceSummary[] }>("GET", `/api/v1/platform/workspaces${buildQueryString({
      query: filters.query?.trim() || undefined,
      status: filters.status,
      limit: filters.limit,
    })}`, token),
  workspaceActivationAnalytics: (token: string, filters: { query?: string; status?: string; limit?: number } = {}) =>
    request<{ analytics: PlatformWorkspaceActivationAnalytics }>("GET", `/api/v1/platform/workspaces/activation-analytics${buildQueryString({
      query: filters.query?.trim() || undefined,
      status: filters.status,
      limit: filters.limit,
    })}`, token),
  platformFleetOverview: (token: string, filters: { query?: string; status?: string; limit?: number } = {}) =>
    request<{ fleet: PlatformFleetOverviewEntry }>("GET", `/api/v1/platform/fleet/overview${buildQueryString({
      query: filters.query?.trim() || undefined,
      status: filters.status,
      limit: filters.limit,
    })}`, token),
  suspendWorkspace: (token: string, tenantId: string, reason: string) =>
    request<WorkspaceLifecycleResult>("POST", `/api/v1/platform/workspaces/${encodeURIComponent(tenantId)}/suspend`, token, { reason }),
  reactivateWorkspace: (token: string, tenantId: string, reason: string) =>
    request<WorkspaceLifecycleResult>("POST", `/api/v1/platform/workspaces/${encodeURIComponent(tenantId)}/reactivate`, token, { reason }),
  auditEvents: (token: string, filters: { tenantId?: string; action?: string; resource?: string; decision?: "allow" | "deny"; limit?: number } = {}) =>
    request<{ entries: PlatformAuditEvent[] }>("GET", `/api/v1/platform/audit/events${buildQueryString({
      tenantId: filters.tenantId?.trim() || undefined,
      action: filters.action?.trim() || undefined,
      resource: filters.resource?.trim() || undefined,
      decision: filters.decision,
      limit: filters.limit,
    })}`, token),
  workspaceSubscription: (token: string, tenantId: string) =>
    request<WorkspaceSubscriptionResult>("GET", `/api/v1/platform/workspaces/${encodeURIComponent(tenantId)}/subscription`, token),
  updateWorkspaceSubscription: (token: string, tenantId: string, input: SubscriptionUpdateInput) =>
    request<WorkspaceSubscriptionResult>("PATCH", `/api/v1/platform/workspaces/${encodeURIComponent(tenantId)}/subscription`, token, input),
  entitlementOverrides: (token: string, tenantId: string) =>
    request<{ overrides: TenantEntitlementOverride[] }>("GET", `/api/v1/platform/workspaces/${encodeURIComponent(tenantId)}/entitlement-overrides`, token),
  createEntitlementOverride: (token: string, tenantId: string, input: EntitlementOverrideInput) =>
    request<EntitlementOverrideResult>("POST", `/api/v1/platform/workspaces/${encodeURIComponent(tenantId)}/entitlement-overrides`, token, input),
  updateEntitlementOverride: (token: string, tenantId: string, overrideId: string, input: EntitlementOverrideInput) =>
    request<EntitlementOverrideResult>("PUT", `/api/v1/platform/workspaces/${encodeURIComponent(tenantId)}/entitlement-overrides/${encodeURIComponent(overrideId)}`, token, input),
  expireEntitlementOverride: (token: string, tenantId: string, overrideId: string, reason: string) =>
    request<EntitlementOverrideResult>("POST", `/api/v1/platform/workspaces/${encodeURIComponent(tenantId)}/entitlement-overrides/${encodeURIComponent(overrideId)}/expire`, token, { reason }),
  packPublishers: (token: string, status?: PackPublisher["status"] | "all") =>
    request<{ publishers: PackPublisher[] }>("GET", `/api/v1/platform/pack-publishers${buildQueryString({
      status: status && status !== "all" ? status : undefined,
    })}`, token),
  registerPackPublisher: (token: string, input: { publisherId: string; displayName: string; contactEmail: string; publicKeyBase64: string; notes?: string }) =>
    request<{ publisher: PackPublisher }>("POST", "/api/v1/platform/pack-publishers", token, input),
  approvePackPublisher: (token: string, publisherId: string) =>
    request<{ publisher: PackPublisher }>("POST", `/api/v1/platform/pack-publishers/${encodeURIComponent(publisherId)}/approve`, token),
  suspendPackPublisher: (token: string, publisherId: string, reason: string) =>
    request<{ publisher: PackPublisher }>("POST", `/api/v1/platform/pack-publishers/${encodeURIComponent(publisherId)}/suspend`, token, { reason }),
  packRevocations: (token: string) => request<{ revocations: PackRevocation[] }>("GET", "/api/v1/platform/pack-revocations", token),
  revokePack: (token: string, input: { packId: string; version: string; reason: string }) =>
    request<{ revocation: PackRevocation }>("POST", "/api/v1/platform/pack-revocations", token, input),
  unrevokePack: (token: string, packId: string, version: string) =>
    request<{ removed: boolean }>("DELETE", `/api/v1/platform/pack-revocations/${encodeURIComponent(packId)}/${encodeURIComponent(version)}`, token),
  marketplacePacks: (token: string, filters: { publisherId?: string; status?: "active" | "yanked" | "all"; reviewStatus?: MarketplacePackReviewStatus | "all"; includeRevoked?: boolean; limit?: number } = {}) =>
    request<{ packs: MarketplacePackEntry[] }>("GET", `/api/v1/platform/marketplace/packs${buildQueryString({
      publisherId: filters.publisherId?.trim() || undefined,
      status: filters.status,
      reviewStatus: filters.reviewStatus,
      includeRevoked: filters.includeRevoked ? "true" : undefined,
      limit: filters.limit,
    })}`, token),
  reviewMarketplacePack: (token: string, packId: string, version: string, input: { status: MarketplacePackReviewStatus; notes?: string; evidenceUrls?: string[] }) =>
    request<{ pack: MarketplacePackEntry }>("POST", `/api/v1/platform/marketplace/packs/${encodeURIComponent(packId)}/${encodeURIComponent(version)}/review`, token, input),
  marketplaceInstallAnalytics: (token: string, filters: { tenantId?: string; packId?: string; limit?: number } = {}) =>
    request<{ analytics: MarketplacePackInstallAnalytics }>("GET", `/api/v1/platform/marketplace/install-analytics${buildQueryString({
      tenantId: filters.tenantId?.trim() || undefined,
      packId: filters.packId?.trim() || undefined,
      limit: filters.limit,
    })}`, token),
  marketplaceReviews: (token: string, filters: { tenantId?: string; packId?: string; version?: string; status?: MarketplaceBuyerReviewStatus | "all"; limit?: number } = {}) =>
    request<{ reviews: MarketplacePackReviewEntry[] }>("GET", `/api/v1/platform/marketplace/reviews${buildQueryString({
      tenantId: filters.tenantId?.trim() || undefined,
      packId: filters.packId?.trim() || undefined,
      version: filters.version?.trim() || undefined,
      status: filters.status && filters.status !== "all" ? filters.status : undefined,
      limit: filters.limit,
    })}`, token),
  moderateMarketplaceReview: (token: string, tenantId: string, reviewId: string, input: { status: MarketplaceBuyerReviewStatus; notes?: string }) =>
    request<{ review: MarketplacePackReviewEntry }>("POST", `/api/v1/platform/marketplace/reviews/${encodeURIComponent(tenantId)}/${encodeURIComponent(reviewId)}/moderate`, token, input),
  connectorPlugins: (token: string, tenantId: string, filters: { status?: ConnectorPluginStatus | "all"; adapter?: ConnectorPluginAdapter | "all" } = {}) =>
    request<{ plugins: ConnectorPluginEntry[] }>("GET", `/api/v1/platform/workspaces/${encodeURIComponent(tenantId)}/connectors/plugins${buildQueryString({
      status: filters.status && filters.status !== "all" ? filters.status : undefined,
      adapter: filters.adapter && filters.adapter !== "all" ? filters.adapter : undefined,
    })}`, token),
  connectorPlugin: (token: string, tenantId: string, pluginId: string, version: string) =>
    request<{ plugin: ConnectorPluginEntry }>("GET", `/api/v1/platform/workspaces/${encodeURIComponent(tenantId)}/connectors/plugins/${encodeURIComponent(pluginId)}/${encodeURIComponent(version)}`, token),
  transitionConnectorPlugin: (token: string, tenantId: string, pluginId: string, version: string, transition: "approve" | "suspend" | "revoke", reason?: string) =>
    request<{ plugin: ConnectorPluginEntry }>("POST", `/api/v1/platform/workspaces/${encodeURIComponent(tenantId)}/connectors/plugins/${encodeURIComponent(pluginId)}/${encodeURIComponent(version)}/${transition}`, token, reason ? { reason } : {}),
  certifyConnectorPlugin: (token: string, tenantId: string, pluginId: string, version: string, notes?: string) =>
    request<{ certification: ConnectorPluginCertification }>("POST", `/api/v1/platform/workspaces/${encodeURIComponent(tenantId)}/connectors/plugins/${encodeURIComponent(pluginId)}/${encodeURIComponent(version)}/certify`, token, notes ? { notes } : {}),
  connectorPluginEvidencePack: (token: string, tenantId: string, pluginId: string, version: string) =>
    request<{ evidencePack: unknown }>("GET", `/api/v1/platform/workspaces/${encodeURIComponent(tenantId)}/connectors/plugins/${encodeURIComponent(pluginId)}/${encodeURIComponent(version)}/evidence-pack`, token),
  tenantReleases: (token: string, tenantId: string, filters: { agentId?: string; environment?: string; status?: string; limit?: number } = {}) =>
    request<{ releases: AgentReleaseEntry[] }>("GET", `/api/v1/platform/workspaces/${encodeURIComponent(tenantId)}/agent-releases${buildQueryString({
      agentId: filters.agentId?.trim() || undefined,
      environment: filters.environment,
      status: filters.status,
      limit: filters.limit,
    })}`, token),
  tenantRollout: (token: string, tenantId: string, agentId: string, environment = "production") =>
    request<{ plan: RolloutPlanEntry | null }>("GET", `/api/v1/platform/workspaces/${encodeURIComponent(tenantId)}/agents/${encodeURIComponent(agentId)}/rollout${buildQueryString({ environment })}`, token),
  rollbackTenantRollout: (token: string, tenantId: string, agentId: string, environment: string, reason: string) =>
    request<{ plan: RolloutPlanEntry | null }>("POST", `/api/v1/platform/workspaces/${encodeURIComponent(tenantId)}/agents/${encodeURIComponent(agentId)}/rollout/rollback-canary${buildQueryString({ environment })}`, token, { reason }),
  tenantRoiSummary: (token: string, tenantId: string) =>
    request<{ summary: unknown }>("GET", `/api/v1/platform/workspaces/${encodeURIComponent(tenantId)}/roi/summary`, token),
  tenantRoiWorkflows: (token: string, tenantId: string) =>
    request<{ workflows: unknown[] }>("GET", `/api/v1/platform/workspaces/${encodeURIComponent(tenantId)}/roi/workflows`, token),
  tenantRoiSnapshots: (token: string, tenantId: string) =>
    request<{ snapshots: unknown[] }>("GET", `/api/v1/platform/workspaces/${encodeURIComponent(tenantId)}/roi/snapshots`, token),
  createTenantRoiSnapshot: (token: string, tenantId: string, snapshotType: "scheduled" | "qbr" | "renewal" | "manual") =>
    request<{ snapshot: unknown }>("POST", `/api/v1/platform/workspaces/${encodeURIComponent(tenantId)}/roi/snapshots`, token, { snapshotType }),
};